/**
 * music-sequencer/timeline.js — window.MSTimeline / module.exports
 *
 * ОДНО описание того, КОГДА что звучит в /music-sequencer. Его читают все три
 * двери тула: живое прослушивание, файл (WAV/MP3/OGG) и MIDI.
 *
 * ── Зачем отдельно ───────────────────────────────────────────────────────
 * До 25.09.2026 у тула было два независимых описания одной и той же музыки, и
 * они расходились:
 *
 *   прослушивание  песня ставилась ОДИН раз, бит крутился, пока человек слушал;
 *   файл           песня ставилась заново в начале КАЖДОГО прохода паттерна,
 *                  а бит играл ровно «4x» прохода и замолкал.
 *
 * При 192 BPM и 16 шагах проход длится 1.25 с: в файле копии песни вступали
 * на 0 / 1.25 / 2.5 / 3.75 с, бочка обрывалась на 4.87 с, а песня дальше шла
 * «каноном» из четырёх копий (замер стендом на настоящей странице, 25.09.2026).
 * Обращение #1754, Премиум, Samsung Internet: «накладываю бит на музыку,
 * нажимаю скачать, а оно просто задваивается жёстко каждые 5 секунд и
 * повторяется» — двенадцать скачиваний во всех форматах, ни одного верного.
 *
 * Поэтому здесь чистая математика — без DOM и без Web Audio — и те же числа
 * проверяются в Node (tests/js/music-sequencer-timeline.test.mjs).
 *
 * ── Понятия ──────────────────────────────────────────────────────────────
 *   шаг        шестнадцатая (так тул устроен всегда: `steps` — число
 *              шестнадцатых в паттерне, а не «разрешение»);
 *   проход     один раз через аранжировку (все паттерны по порядку);
 *   шаг n      сквозной номер шага от начала бита (n = 0 — первая доля);
 *   позиция    секунды на шкале; 0 — момент шага 0. Клип может начаться
 *              раньше нуля (вступление песни до первой доли бита).
 *   карта      функция «шаг → позиция». Две формы:
 *                gridMap   ровная сетка BPM со свингом;
 *                followMap бит идёт по долям ПЕСНИ (сетка BeatGrid), поэтому
 *                          не разъезжается с живой записью, у которой темп
 *                          гуляет (замер tempo-straighten: у половины наших же
 *                          образцов Suno доли уходят от лучшей ровной сетки
 *                          больше чем на 100 мс).
 *
 * ── Свинг ────────────────────────────────────────────────────────────────
 * Свинг ЗАДЕРЖИВАЕТ слабую шестнадцатую (нечётный шаг). До 25.09 реализация
 * делала наоборот — нечётный шаг приходил РАНЬШЕ (чётный шаг получал
 * укороченную длительность), то есть «свинг» тянул грув вперёд. 100 % = 1/3
 * шестнадцатой = триольное ощущение (2 : 1).
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.MSTimeline = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var SWING_MAX = 1 / 3;
    /* Шагов в такте 4/4. Сдвиг клипа «на такт» и повтор клипа «по тактам»
       меряются этим числом при любом размере паттерна. */
    var STEPS_PER_BAR = 16;
    var STEPS_PER_QUARTER = 4;
    /* Предел числа шагов, на которое бит может вытянуться под песню: 20 минут
       при 300 BPM — гарантия от бесконечного цикла на испорченной карте. */
    var MAX_STEPS = 20 * 60 * 20;

    function isNum(v) { return typeof v === 'number' && isFinite(v); }
    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    function swingShift(n, k) {
        // Нечётный шаг (слабая шестнадцатая) приходит позже на k шестнадцатой.
        return ((n % 2) + 2) % 2 === 1 ? k : 0;
    }

    /* ── Проход ─────────────────────────────────────────────────────────── */

    /**
     * @param {number[]} slotSteps  число шагов в каждом слоте аранжировки
     * @returns {{total:number, starts:number[], slotSteps:number[], locate:Function}}
     */
    function pass(slotSteps) {
        var starts = [], total = 0, i;
        var clean = [];
        for (i = 0; i < (slotSteps || []).length; i++) {
            var s = Math.max(0, Math.floor(slotSteps[i] || 0));
            clean.push(s);
            starts.push(total);
            total += s;
        }
        return {
            total: total,
            starts: starts,
            slotSteps: clean,
            /** Сквозной шаг n → {slot, step, pass}. Бит крутится по кругу. */
            locate: function (n) {
                if (!(total > 0)) return null;
                var p = Math.floor(n / total);
                var r = n - p * total;
                var slot = 0;
                while (slot + 1 < starts.length && starts[slot + 1] <= r) slot++;
                return { slot: slot, step: r - starts[slot], pass: p };
            }
        };
    }

    /* ── Карты ──────────────────────────────────────────────────────────── */

    function gridMap(bpm, swing) {
        var b = clamp(isNum(bpm) && bpm > 0 ? bpm : 120, 1, 1000);
        var base = 60 / b / STEPS_PER_QUARTER;
        var k = clamp(isNum(swing) ? swing : 0, 0, 100) / 100 * SWING_MAX;
        return {
            kind: 'grid',
            bpm: b,
            swingK: k,
            origin: 0,
            at: function (n) { return (n + swingShift(n, k)) * base; },
            quarterAt: function (x) { return x * STEPS_PER_QUARTER * base; },
            bpmAt: function () { return b; },
            /** Ближайший шаг к позиции pos (для записи с клавиатуры). */
            stepNear: function (pos) {
                var n = Math.round(pos / base);
                var best = n, bestD = Infinity;
                for (var d = -1; d <= 1; d++) {
                    var m = n + d, dd = Math.abs(this.at(m) - pos);
                    if (dd < bestD) { bestD = dd; best = m; }
                }
                return best;
            }
        };
    }

    /* Средний интервал края сетки — им продолжаем её за пределы песни. */
    function edgeInterval(beats, fromEnd) {
        var n = beats.length, k = Math.min(8, n - 1);
        if (k < 1) return 0.5;
        return fromEnd ? (beats[n - 1] - beats[n - 1 - k]) / k : (beats[k] - beats[0]) / k;
    }

    /** Время доли с дробным номером idx по сетке песни (линейно между долями,
     *  за краями — продолжение средним интервалом края). */
    function beatTime(beats, idx) {
        var n = beats.length;
        if (idx <= 0) return beats[0] + idx * edgeInterval(beats, false);
        if (idx >= n - 1) return beats[n - 1] + (idx - (n - 1)) * edgeInterval(beats, true);
        var i = Math.floor(idx), f = idx - i;
        return beats[i] + (beats[i + 1] - beats[i]) * f;
    }

    /**
     * Бит идёт по долям песни.
     * @param {{beats:number[], ratio?:number, j0?:number, swing?:number}} o
     *   beats  доли песни в секундах ОТ НАЧАЛА ФАЙЛА песни (BeatGrid.track);
     *   ratio  сколько наших четвертей приходится на одну найденную долю:
     *          1 — трекер нашёл тот же уровень, 2 — нашёл вдвое медленнее,
     *          0.5 — вдвое быстрее (известное свойство трекера, см. beat-grid.js);
     *   j0     номер доли песни (может быть дробным), на которой стоит шаг 0.
     * Позиция 0 = доля j0; песня как клип начинается в позиции −origin.
     */
    function followMap(o) {
        var beats = (o && o.beats) || [];
        if (beats.length < 4) return null;
        var ratio = o.ratio > 0 ? o.ratio : 1;
        var j0 = isNum(o.j0) ? o.j0 : 0;
        var k = clamp(isNum(o.swing) ? o.swing : 0, 0, 100) / 100 * SWING_MAX;
        var origin = beatTime(beats, j0);
        function quarterAt(x) { return beatTime(beats, j0 + x / ratio) - origin; }
        return {
            kind: 'follow',
            ratio: ratio,
            j0: j0,
            swingK: k,
            origin: origin,
            at: function (n) { return quarterAt((n + swingShift(n, k)) / STEPS_PER_QUARTER); },
            quarterAt: quarterAt,
            /** Местный темп вокруг шага n. */
            bpmAt: function (n) {
                var q = n / STEPS_PER_QUARTER;
                var d = quarterAt(q + 1) - quarterAt(q);
                return d > 0 ? 60 / d : 0;
            },
            stepNear: function (pos) {
                // Грубо по среднему темпу, потом уточняем соседями.
                var avg = (quarterAt(8) - quarterAt(0)) / 8 / STEPS_PER_QUARTER;
                var n = Math.round(pos / (avg > 0 ? avg : 0.1));
                for (var guard = 0; guard < 64; guard++) {
                    var here = Math.abs(this.at(n) - pos);
                    if (Math.abs(this.at(n + 1) - pos) < here) n++;
                    else if (Math.abs(this.at(n - 1) - pos) < here) n--;
                    else break;
                }
                return n;
            }
        };
    }

    /** Медианный темп сетки песни в BPM (для показа и выбора уровня). */
    function medianBpm(beats) {
        if (!beats || beats.length < 3) return null;
        var d = [];
        for (var i = 1; i < beats.length; i++) d.push(beats[i] - beats[i - 1]);
        d.sort(function (a, b) { return a - b; });
        var m = d[d.length >> 1];
        return m > 0 ? 60 / m : null;
    }

    /**
     * Какой уровень сетки песни взять: из найденного темпа, его половины и
     * удвоения — ближайший (в октавах) к темпу, который человек уже выставил.
     * Выставленный BPM — лучший ответ на вопрос, который трекер решает хуже
     * всего (метрический уровень, шапка beat-grid.js).
     * @returns {number} ratio для followMap
     */
    function pickRatio(songBpm, userBpm) {
        if (!(songBpm > 0) || !(userBpm > 0)) return 1;
        var best = 1, bestD = Infinity, cands = [0.5, 1, 2];
        for (var i = 0; i < cands.length; i++) {
            var d = Math.abs(Math.log(songBpm * cands[i] / userBpm));
            if (d < bestD - 1e-9) { bestD = d; best = cands[i]; }
        }
        return best;
    }

    /* ── Клипы ──────────────────────────────────────────────────────────── */

    /** Позиция начала клипа (его первого куска). */
    function clipStart(c, map) {
        if (!c || !map) return 0;
        var base = isNum(c.anchorStart) ? c.anchorStart : map.at(c.startStep || 0);
        return base + (isNum(c.nudge) ? c.nudge : 0);
    }

    /**
     * Где на шкале звучит клип (запись с микрофона или загруженный файл).
     * @param {{dur:number, startStep?:number, nudge?:number, anchorStart?:number,
     *          loop?:boolean, loopSteps?:number}} c
     *   anchorStart  фиксированная позиция начала (песня, по которой идёт бит);
     *                такой клип не повторяется;
     *   startStep    иначе — шаг вступления (сдвиг «на такт» = 16 шагов);
     *   nudge        точная поправка в секундах (задержка записи и т.п.);
     *   loop         повторять каждые loopSteps шагов; повтор обрывает
     *                предыдущий кусок, как у сэмплера.
     * @param {object} map
     * @param {number} from  позиция начала окна
     * @param {number} to    позиция конца окна
     * @returns {{at:number, offset:number, len:number, iter:number}[]}
     *   at — позиция старта куска в окне, offset — с какой секунды клипа (окно
     *   открылось посреди клипа), len — сколько секунд клипа звучит.
     */
    function clipPlacements(c, map, from, to) {
        var out = [];
        if (!c || !(c.dur > 0) || !map) return out;
        var loop = !!c.loop && !isNum(c.anchorStart);
        if (!loop) {
            var at0 = clipStart(c, map);
            if (at0 < to && at0 + c.dur > from) {
                var off0 = at0 < from ? from - at0 : 0;
                out.push({ at: at0 + off0, offset: off0, len: c.dur - off0, iter: 0 });
            }
            return out;
        }
        var L = Math.max(1, Math.floor(c.loopSteps || STEPS_PER_BAR));
        var s0 = c.startStep || 0;
        var nudge = isNum(c.nudge) ? c.nudge : 0;
        for (var i = 0; i < MAX_STEPS; i++) {
            var at = map.at(s0 + i * L) + nudge;
            if (at >= to) break;
            var next = map.at(s0 + (i + 1) * L) + nudge;
            var len = Math.min(c.dur, next - at);
            if (at + len > from) {
                var off = at < from ? from - at : 0;
                out.push({ at: at + off, offset: off, len: len - off, iter: i });
            }
        }
        return out;
    }

    /* ── План файла ─────────────────────────────────────────────────────── */

    /**
     * Сколько шагов играет бит и какой отрезок шкалы идёт в файл.
     * @param {{pass:object, map:object, loops:number, trimTail?:number,
     *          beatEmpty?:boolean, clips?:object[], tailSec?:number}} o
     *   trimTail   пустые шаги в конце ПОСЛЕДНЕГО слота — срезаются только
     *              когда бит звучит один раз и под ним нет клипов (раньше тул
     *              срезал хвост у КАЖДОГО паттерна аранжировки, и следующий
     *              паттерн вступал раньше своей доли);
     *   clips      [{dur, audibleDur?, ...клип}] — слышимые клипы;
     *   tailSec    хвост после последнего звука (затухание, реверб).
     * @returns {{N:number, beatEnd:number, songEnd:number, spanStart:number,
     *            spanEnd:number, byClip:boolean}}
     *
     * Правило длины: файл длится, пока звучит САМОЕ длинное из двух — бит
     * (loops проходов) или клипы (песня целиком). Бит крутится под песней до
     * её конца, как в прослушивании; повторяющиеся клипы длину не задают, они
     * лишь заполняют её.
     */
    function plan(o) {
        var P = o.pass ? o.pass.total : 0;
        var loops = Math.max(1, Math.floor(o.loops || 1));
        var map = o.map;
        var clips = o.clips || [];
        var tail = isNum(o.tailSec) ? o.tailSec : 0.5;

        var spanStart = 0, songEnd = 0, hasClips = false, i;
        for (i = 0; i < clips.length; i++) {
            var c = clips[i];
            if (!c || !(c.dur > 0)) continue;
            hasClips = true;
            var s = clipStart(c, map);
            if (s < spanStart) spanStart = s;
            var audible = c.audibleDur > 0 ? Math.min(c.audibleDur, c.dur) : c.dur;
            var e = s + audible;
            if (e > songEnd) songEnd = e;
        }

        var N = 0, loopsEnd = 0;
        if (!o.beatEmpty && P > 0) {
            N = loops * P;
            if (loops === 1 && !hasClips) N = Math.max(0, P - Math.max(0, o.trimTail || 0));
            loopsEnd = map.at(N);
            if (hasClips) {
                var guard = 0;
                while (map.at(N) < songEnd - 1e-6 && guard++ < MAX_STEPS) N++;
            }
        }
        var beatEnd = N > 0 ? map.at(N) : 0;
        return {
            N: N,
            beatEnd: beatEnd,
            songEnd: songEnd,
            spanStart: spanStart,
            spanEnd: Math.max(beatEnd, songEnd) + tail,
            // the clips, not «4x», decide how long the file is
            byClip: hasClips && songEnd > loopsEnd + 1e-6
        };
    }

    /** Тик MIDI для шага n (свинг — смещением тика, как в звуке). */
    function midiTick(n, ticksPerStep, swingK) {
        return Math.round((n + swingShift(n, swingK || 0)) * ticksPerStep);
    }

    return {
        SWING_MAX: SWING_MAX,
        STEPS_PER_BAR: STEPS_PER_BAR,
        STEPS_PER_QUARTER: STEPS_PER_QUARTER,
        pass: pass,
        gridMap: gridMap,
        followMap: followMap,
        beatTime: beatTime,
        medianBpm: medianBpm,
        pickRatio: pickRatio,
        clipPlacements: clipPlacements,
        clipStart: clipStart,
        plan: plan,
        midiTick: midiTick
    };
});
